import {BaseEnumDeserializer} from '../../common';
import {fail, Result, some} from '../../../utils';

export enum LaancFrRules {
  /**
   * (Block) [3.3.6a] The USS must not submit any operations to a given facility that includes a grid with a LAANC Ready
   * flag of “false” for that facility. Furthermore, the USS must advise when a LAANC Grid is disabled and direct users
   * with a message to DroneZone.
   */
  UASFM_NOT_ENABLED = 'UASFM_NOT_ENABLED',

  /**
   * (Block) [3.3.6b] Operation requires 107-FC in a UASFM that is enabled but doesn't support 107-FC
   * The USS must only submit an operation to a given facility if the “Enabled” field indicates that the operation type
   * is available for that facility through LAANC.
   */
  UASFM_107_FC_UNAVAILABLE = 'UASFM_107_FC_UNAVAILABLE',

  /**
   * (Block) [3.3.6b] Operation Requires 44809-AA in a UASFM that is enabled but doesn't support 44809-AA
   * The USS must only submit an operation to a given facility if the “Enabled” field indicates that the operation type
   * is available for that facility through LAANC.
   */
  UASFM_44809_AA_UNAVAILABLE = 'UASFM_44809_AA_UNAVAILABLE',

  /**
   * (Block) Block an operator from submitting an operation within a surface airspace that has no UASFM
   */
  AIRSPACE_NO_UASFM = 'AIRSPACE_NO_UASFM',

  /**
   * (Block) [3.4.5b] Operations exceeding 400 feet (107.51b) are not supported by LAANC.
   */
  EXCEEDING_400_FT = 'EXCEEDING_400_FT',

  /**
   * (Advise) [3.4.5c] Part 107 Operations at night (107.29)
   * USSs must alert operators that at night, operating a sUAS is only permitted if: (1) the operator has completed the
   * required FAA training/testing, and (2) the sUAS is lighted with anti-collision lighting visible for at least 3
   * statute miles that has a flash rate sufficient to avoid a collision.
   * USSs must employ a reasonable algorithm for periods of nighttime excluding civil twilight.
   */
  AT_NIGHT_PART_107 = 'AT_NIGHT_PART_107',

  /**
   * (Advise) [3.4.5d] 44809 Operations at night
   * USSs must alert operators that at night, "It is recommended that sUAS should be lit with anti-collision lighting
   * visible for at least 3 statute miles away and arrange lights on the UA in such a way that allows recreational flyers
   * to determine the orientation and flight path of the aircraft. For additional guidance, refer to “Night Flight” in
   * Advisory Circular 91-57. [Provide a link to the most recent revision of Advisory Circular 91-57.]”
   * USSs must employ a reasonable algorithm for periods of nighttime excluding civil twilight.
   */
  AT_NIGHT_44809 = 'AT_NIGHT_44809',

  /**
   * (Block) [3.4.5e] Operations in an NSUFR or the DC FRZ (107.47)
   * This request type is not supported by LAANC. NSUFRs may be full-time or part-time (see respective datasets). The DC
   * FRZ is full-time.
   */
  IN_NSUFR = 'IN_NSUFR',

  /**
   * (Block) [3.4.5e] Operations in an NSUFR or the DC FRZ (107.47)
   * This request type is not supported by LAANC. NSUFRs may be full-time or part-time (see respective datasets). The DC
   * FRZ is full-time.
   * @Deprecated Use SFRA_DC_INNER instead
   */
  IN_DC_FRZ = 'IN_DC_FRZ',

  /**
   * (Block) [3.4.5f] Operations in a Prohibited or active Restricted SUA (107.45)
   * USS may use active times from an FAA source (see Table 1 and sua.faa.gov) In order to meet this rule without the
   * burden of processing active times, USSs may block all Prohibited and Restricted SUAs all the time.
   */
  IN_PROHIBITED_OR_RESTRICT_SUA = 'IN_PROHIBITED_OR_RESTRICT_SUA',

  /**
   * (Advise) [3.4.5g] Operations at civil twilight (107.29)
   * USSs must alert operators that during civil twilight periods, operating a sUAS is only permitted if the sUAS is
   * lighted with anti-collision lighting visible for at least 3 statute miles that has a flash rate sufficient to avoid
   * a collision.
   */
  AT_CIVIL_TWILIGHT = 'AT_CIVIL_TWILIGHT',

  /**
   * (Block) [3.4.5h] Operations in a TFR (107.47)
   * The USS must ingest digital TFRs from an accepted source (see Table 1) and display them. Blocking new operations
   * and invalidating existing operations is permissible but not required.
   */
  IN_TFR = 'IN_TFR',

  /**
   * (Advise) [3.4.5i] Operations in another type of SUA (MOA, CFA, Warning, Alert, etc.) (107.49)
   * Providing FAA link(s) minimally satisfies this rule. USSs may read digital SUAs for these other types and display
   * them. Blocking is permissible but not required.
   */
  IN_OTHER_SUA_TYPE = 'IN_OTHER_SUA_TYPE',

  /**
   * (Block) [3.4.2b] Further Coordination requests must be submitted no later than 72 hours prior to the start time of
   * the requested authorization.
   */
  TOO_SOON_FOR_FC = 'TOO_SOON_FOR_FC',

  /**
   * (Block) [3.4.10d] Pilot Has Unacknowledged Authorization(s)
   * When an operation is rescinded, the USS must not allow further submission of authorization requests until the
   * authorization is complete or de-authorized.
   */
  UNACKNOWLEDGED_AUTH = 'UNACKNOWLEDGED_AUTH',

  /**
   * (Block) [3.7c] Block any submissions associated with a new operation if there are already five (or
   * more) non-pending operations for the same operator (name and phone number) occurring
   * with an overlapping time period. A message should be generated to inform the user of the
   * reason for the block.
   */
  MAX_OPERATIONS = 'MAX_OPERATIONS',

  /**
   * (Block) [3.7b] Block submissions with an operation area larger, in maximum linear extent, than 10
   * nautical miles.
   */
  AREA_TOO_LARGE = 'AREA_TOO_LARGE',

  /**
   * (Block) [3.7d] Block submissions (automatic or Further Coordination) that are more than 100 nautical
   * miles from an existing non-pending operation for the same operator (name and phone
   * number) for an overlapping time period. Specifically, two operation volumes are more than
   * 100 nautical miles apart if the nearest points between the two volumes are a distance greater
   * than 100 nautical miles from each other. A message should be generated to inform the user of
   * the reason for the block.
   */
  OVER_100NM_FROM_EXISTING_OPERATION = 'OVER_100NM_FROM_EXISTING_OPERATION',

  /**
   * (Advise) [3.7e] If a new submission is made when there is one (or more) non-pending operations for the
   * same operator (name and phone number) occurring with an overlapping time period, the USS
   * must display a message to user that there are one or more operations for this operator and time
   * period and unused operations should be cancelled before the operation start time.
   */
  EXISTING_OPERATION = 'EXISTING_OPERATION',

  /**
   * (Block) [3.8a] Short term unexpected LAANC downtime or connection issues to LAANC System. Temporary outage, try
   * again later.
   * [...] The USS could inform the operator that the FAA’s LAANC system interface is temporarily unavailable, and they
   * will be notified when the process is complete (or should check back before flying).
   */
  LAANC_DOWN_TEMP = 'LAANC_DOWN_TEMP',

  /**
   * (Block) [3.8b] Unexpected LAANC downtime or connection issues persisting over 4 hours.
   * In the event of protracted unavailability of the FAA's LAANC system lasting more than 4 hours, or at the direction
   * of the FAA, USSs must make the following statement available to users in a manner appropriate to its application
   * design: [see performance rules]
   */
  LAANC_DOWN_PROTRACTED = 'LAANC_DOWN_PROTRACTED',

  /**
   * (Block) LAANC down for scheduled reasons.
   */
  LAANC_DOWN_SCHEDULED = 'LAANC_DOWN_SCHEDULED',

  /**
   * (Block) Airspace data not available to perform LAANC check
   */
  LOCAL_AIRSPACE_DATA_UNAVAILABLE = 'LOCAL_AIRSPACE_DATA_UNAVAILABLE',

  /**
   * (Block inner/Advise outer) [3.4.5k] Operations within the DC Special Flight Rules Area (SFRA)
   * Flying a drone for recreational or nonrecreational use between 15 and 30 miles from Washington, D.C. is allowed
   * under specific operating conditions outlined at the following link.
   * https://www.faa.gov/uas/resources/community_engagement/no_drone_zone/dc. Operations in the inner 15 miles from
   * Washington, D.C. are not allowed and must be blocked.
   */
  SFRA_DC_INNER = 'SFRA_DC_INNER',
  SFRA_DC_OUTER = 'SFRA_DC_OUTER',

  /**
   * (Advise) [3.4.6a] Class E Surface Area Weather Ceiling Caveat
   * The USS must inform operators of the Class E limitation when it applies to their planned operation. The
   * authorization caveat is necessary to alert the operator of the spacing rules in Class E airspace which allows for
   * low flying aircraft when a low weather ceiling exists. The USS should also assist in informing the operator about
   * relevant, reliable weather information to include current and forecast sky condition that describes cloud cover,
   * vertical visibility, or clear skies and the height of the layer indicated in feet Above Ground Level (AGL).
   * Providing a link to an aviation weather source that includes Meteorological Aerodrome Report (METAR) and Terminal
   * Aerodrome Forecasts (TAF) reports is strongly recommended. The USS may also refer to the Aviation Weather Services
   * Advisory Circular for detailed information about sky condition reporting criteria (AC 00-45, Section 3.1.5.9).
   */
  CLASS_E_AIRSPACE = 'CLASS_E_AIRSPACE',

  STADIUM_EVENT = 'STADIUM_EVENT'
}

export class LaancFrRulesUtil implements BaseEnumDeserializer<LaancFrRules, LaancFrRules> {
  humanized: {[key in LaancFrRules]: string} = {
    [LaancFrRules.UASFM_NOT_ENABLED]: 'LAANC Not Enabled in This Area',
    [LaancFrRules.UASFM_107_FC_UNAVAILABLE]: 'Part 107 Further Coordination Operations Not Permitted in This Area',
    [LaancFrRules.UASFM_44809_AA_UNAVAILABLE]: 'Section 44809 Auto-Approval Operations Not Permitted in This Area',
    [LaancFrRules.AIRSPACE_NO_UASFM]: 'Airspace Does Not Have UAS Facility Map',
    [LaancFrRules.EXCEEDING_400_FT]: 'Exceeds 400FT',
    [LaancFrRules.AT_NIGHT_PART_107]: 'At Night (Part 107)',
    [LaancFrRules.AT_NIGHT_44809]: 'At Night (Section 44809)',
    [LaancFrRules.IN_NSUFR]: 'In National Security UAS Flight Restriction',
    [LaancFrRules.IN_DC_FRZ]: 'In DC Flight-Restricted Zone',
    [LaancFrRules.IN_PROHIBITED_OR_RESTRICT_SUA]: 'In Prohibited or Restricted Special Use Airspace',
    [LaancFrRules.AT_CIVIL_TWILIGHT]: 'At Civil Twilight',
    [LaancFrRules.IN_TFR]: 'In Temporary Flight Restriction',
    [LaancFrRules.IN_OTHER_SUA_TYPE]: 'In Other Special Use Airspace Type',
    [LaancFrRules.TOO_SOON_FOR_FC]: 'Further Coordination Operations Must Start at Least 72 Hrs in Advance',
    [LaancFrRules.UNACKNOWLEDGED_AUTH]: 'Pilot Has Unacknowledged Authorization(s)',
    [LaancFrRules.MAX_OPERATIONS]: 'Maximum of 5 Simultaneous Non-Pending Operations Exceeded',
    [LaancFrRules.AREA_TOO_LARGE]: 'Operation Area Exceeds 10 NM',
    [LaancFrRules.OVER_100NM_FROM_EXISTING_OPERATION]: 'Over 100NM From an Existing Operation',
    [LaancFrRules.EXISTING_OPERATION]: 'An Existing Operation Is Occurring at the Same Time',
    [LaancFrRules.LAANC_DOWN_TEMP]: 'LAANC System Is Temporarily Unavailable. Please Try Again Later.',
    [LaancFrRules.LAANC_DOWN_PROTRACTED]: 'LAANC System Is Unavailable',
    [LaancFrRules.LAANC_DOWN_SCHEDULED]: 'LAANC System Is Down For Scheduled Maintenance. Please Try Again Later.',
    [LaancFrRules.LOCAL_AIRSPACE_DATA_UNAVAILABLE]: 'Local Airspace Data Unavailable',
    [LaancFrRules.SFRA_DC_INNER]: 'In DC Special Flight Rules Area (SFRA)',
    [LaancFrRules.SFRA_DC_OUTER]: 'In DC Special Flight Rules Area (SFRA)',
    [LaancFrRules.CLASS_E_AIRSPACE]: 'Class E Surface Area Weather Ceiling Caveat',
    [LaancFrRules.STADIUM_EVENT]: 'During a Stadium Event'
  }

  deserialize(raw: unknown): Result<LaancFrRules> {
    if (typeof raw !== 'string') return fail('Invalid type for LAANC FR rules');

    switch (raw.toUpperCase()) {
      case 'UASFM_NOT_ENABLED':
        return some(LaancFrRules.UASFM_NOT_ENABLED);
      case 'UASFM_107_FC_UNAVAILABLE':
        return some(LaancFrRules.UASFM_107_FC_UNAVAILABLE);
      case 'UASFM_44809_AA_UNAVAILABLE':
        return some(LaancFrRules.UASFM_44809_AA_UNAVAILABLE);
      case 'AIRSPACE_NO_UASFM':
        return some(LaancFrRules.AIRSPACE_NO_UASFM);
      case 'EXCEEDING_400_FT':
        return some(LaancFrRules.EXCEEDING_400_FT);
      case 'AT_NIGHT_PART_107':
        return some(LaancFrRules.AT_NIGHT_PART_107);
      case 'AT_NIGHT_44809':
        return some(LaancFrRules.AT_NIGHT_44809);
      case 'IN_NSUFR':
        return some(LaancFrRules.IN_NSUFR);
      case 'IN_DC_FRZ':
        return some(LaancFrRules.IN_DC_FRZ);
      case 'IN_PROHIBITED_OR_RESTRICT_SUA':
        return some(LaancFrRules.IN_PROHIBITED_OR_RESTRICT_SUA);
      case 'AT_CIVIL_TWILIGHT':
        return some(LaancFrRules.AT_CIVIL_TWILIGHT);
      case 'IN_TFR':
        return some(LaancFrRules.IN_TFR);
      case 'IN_OTHER_SUA_TYPE':
        return some(LaancFrRules.IN_OTHER_SUA_TYPE);
      case 'TOO_SOON_FOR_FC':
        return some(LaancFrRules.TOO_SOON_FOR_FC);
      case 'UNACKNOWLEDGED_AUTH':
        return some(LaancFrRules.UNACKNOWLEDGED_AUTH);
      case 'MAX_OPERATIONS':
        return some(LaancFrRules.MAX_OPERATIONS);
      case 'AREA_TOO_LARGE':
        return some(LaancFrRules.AREA_TOO_LARGE);
      case 'OVER_100NM_FROM_EXISTING_OPERATION':
        return some(LaancFrRules.OVER_100NM_FROM_EXISTING_OPERATION);
      case 'EXISTING_OPERATION':
        return some(LaancFrRules.EXISTING_OPERATION);
      case 'LAANC_DOWN_TEMP':
        return some(LaancFrRules.LAANC_DOWN_TEMP);
      case 'LAANC_DOWN_PROTRACTED':
        return some(LaancFrRules.LAANC_DOWN_PROTRACTED);
      case 'LAANC_DOWN_SCHEDULED':
        return some(LaancFrRules.LAANC_DOWN_SCHEDULED);
      case 'LOCAL_AIRSPACE_DATA_UNAVAILABLE':
        return some(LaancFrRules.LOCAL_AIRSPACE_DATA_UNAVAILABLE);
      case 'SFRA_DC_INNER':
        return some(LaancFrRules.SFRA_DC_INNER);
      case 'SFRA_DC_OUTER':
        return some(LaancFrRules.SFRA_DC_OUTER);
      case 'CLASS_E_AIRSPACE':
        return some(LaancFrRules.CLASS_E_AIRSPACE);
      case 'STADIUM_EVENT':
        return some(LaancFrRules.STADIUM_EVENT);
      default:
        return fail(`Invalid LAANC FR rules: ${raw}`);
    }
  }

  deserializeArray(raw: unknown): Result<LaancFrRules[]> {
    if (!Array.isArray(raw)) return fail('Invalid type for LAANC FR rules array');
    const results: LaancFrRules[] = [];
    let error = '';
    raw.some((val: any) => {
      let result = this.deserialize(val);
      if (result.type === 'error') {
        error = result.message;
        return true;
      } else {
        results.push(result.value);
        return false;
      }
    });
    return error ? fail(error) : some(results);
  }
}

export const DEFAULT_LAANC_FR_RULES_UTIL = new LaancFrRulesUtil();
